import { useContext, useState } from "react";
import { ButtonDS } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import {
  Field,
  inputStyle,
  ModificationModal,
  modalDescStyle,
  modalTitleStyle,
  SummaryRow,
  summaryBoxStyle,
} from "./shared";

interface NameChangeFlowProps {
  onClose: () => void;
  onSubmitted: () => void;
}

type Step = "form" | "confirm";

const NameChangeFlow = ({ onClose, onSubmitted }: NameChangeFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastName2, setLastName2] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const formValid = firstName.trim() !== "" && lastName.trim() !== "";
  const t = (key: string) => translate(`newDashboard.modifications.name.${key}`);

  const fullName = `${firstName.trim()} ${lastName.trim()}${
    lastName2.trim() !== "" ? ` ${lastName2.trim()}` : ""
  }`;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const repository = new HttpLeadRepository(leadId, token);
      await repository.postModificationRequest("NAME_RECTIFICATION", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        lastName2: lastName2.trim() !== "" ? lastName2.trim() : null,
      });
      onSubmitted();
      setNotification({
        message: translate("newDashboard.modifications.common.successToast"),
        type: "success",
        time: 6,
      });
      onClose();
    } catch (error: any) {
      const key =
        error?.status === 409
          ? "newDashboard.modifications.errors.alreadyInProgress"
          : "newDashboard.modifications.errors.generic";
      setNotification({ message: translate(key), type: "error", time: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModificationModal onClose={onClose}>
      {step === "form" && (
        <div>
          <h2 style={modalTitleStyle}>{t("title")}</h2>
          <p style={modalDescStyle}>{t("desc")}</p>
          <Field
            label={t("firstName")}
            error={showErrors && firstName.trim() === "" ? t("required") : undefined}
          >
            <input
              style={inputStyle}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Field>
          <Field
            label={t("lastName")}
            error={showErrors && lastName.trim() === "" ? t("required") : undefined}
          >
            <input
              style={inputStyle}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
          <Field label={t("lastName2")}>
            <input
              style={inputStyle}
              value={lastName2}
              onChange={(e) => setLastName2(e.target.value)}
              placeholder={t("optional")}
            />
          </Field>
          <ButtonDS
            label={translate("newDashboard.modifications.common.continue")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={() => {
              if (!formValid) return setShowErrors(true);
              setStep("confirm");
            }}
          />
        </div>
      )}

      {step === "confirm" && (
        <div>
          <h2 style={modalTitleStyle}>{t("confirmTitle")}</h2>
          <p style={modalDescStyle}>
            {translate("newDashboard.modifications.common.confirmSignatureDesc")}
          </p>
          <div style={summaryBoxStyle}>
            <SummaryRow label={t("summaryName")} value={fullName} />
          </div>
          <ButtonDS
            label={translate("newDashboard.modifications.common.confirmAndSign")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={submit}
            disabled={submitting}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("form")}
          />
        </div>
      )}

    </ModificationModal>
  );
};

export default NameChangeFlow;
